/*
 * Storefront dev mode — the rules that need no request, so `node --test` can cover them (documents/helpers/devMode.md).
 * Asked for by jument (a developer's token has the live site's replica answer at the live address); shared by every
 * site of this core. Nothing here changes a thing for a visitor without the `_dev_mode` cookie.
 *
 * Relative imports only: node --test does not know the @core alias.
 */

// the cookies dev mode lives in; set and cleared by /apis/dev-mode only
export const DEV_MODE_COOKIES = {
	token: "_dev_mode", // the developer's token (the pass)
	site: "_dev_site", // which replica of the live site
	api: "_dev_api", // the API's key in the backend's list
	api_url: "_dev_api_url", // its GraphQL url, as /apis/dev-mode checked it
};

// in dev mode the visitor's login and cart are the replica's, kept apart from the live site's
export const DEV_RENAMED_COOKIES = {
	_token: "_dev_token",
	_access_id: "_dev_access_id",
	"local-cart": "_dev_local-cart",
};

export const dev_cookie_name = (name, in_dev_mode) => (in_dev_mode && DEV_RENAMED_COOKIES[name]) || name;

// every cookie dev mode may have written, for leaving it
export const ALL_DEV_COOKIES = [...Object.values(DEV_MODE_COOKIES), ...Object.values(DEV_RENAMED_COOKIES)];

// where to go back to: a path of this site only (no "//host", no "/\host", no header injection)
export const safe_back_path = (back) => {
	const text = String(back || "/");
	return /^\/(?![/\\])/.test(text) && !/[\r\n\t]/.test(text) ? text : "/";
};

const LOCAL_API = /^http:\/\/localhost:([0-9]{1,5})\/api\/graphql$/;
export const local_api_url = (port) => {
	const number = Number(port);
	return Number.isInteger(number) && number > 0 && number < 65536 ? `http://localhost:${number}/api/graphql` : null;
};

/*
 * The API a developer chose, checked against the backend's list (checkDevModeToken). "local" is a developer's own
 * web-server on a port they give, and is allowed on any site: their browser calls it. The server only calls it when the
 * app itself runs locally (`api_url_allowed`); a deployed one renders its pages through the site's own API instead,
 * since it cannot reach a developer's machine and must not be made to call ports of its own host.
 * -> { ok: true, api_key, api_url } | { ok: false, reason }
 */
export const choose_api = ({ api_key, port = null, endpoints = [] }) => {
	if (!api_key) {
		return { ok: true, api_key: null, api_url: null };
	}
	const endpoint = endpoints.find((row) => row?.key === api_key);
	if (!endpoint) {
		return { ok: false, reason: "unknown_api" };
	}
	if (endpoint.needs_port) {
		const api_url = local_api_url(port);
		return api_url ? { ok: true, api_key, api_url } : { ok: false, reason: "bad_port" };
	}
	return /^https?:\/\//.test(String(endpoint.url || "")) ? { ok: true, api_key, api_url: endpoint.url } : { ok: false, reason: "unknown_api" };
};

// the replica a developer chose, among the token's; none asked: the first
export const choose_site = ({ site_id, sites = [] }) => {
	const ids = sites.map((site) => String(site?.id));
	if (!ids.length) {
		return { ok: false, reason: "no_sites" };
	}
	if (!site_id) {
		return { ok: true, site_id: ids[0] };
	}
	return ids.includes(String(site_id)) ? { ok: true, site_id: String(site_id) } : { ok: false, reason: "site_not_allowed" };
};

// whether a url read back from the `_dev_api_url` cookie may be called by the server (the cookie is the browser's):
// a localhost url only by an app that runs locally — elsewhere the server keeps the site's own API
export const api_url_allowed = ({ api_url, endpoints = [], app_is_local = false }) => {
	if (!api_url) {
		return false;
	}
	if (endpoints.some((row) => !row?.needs_port && row?.url === api_url)) {
		return true;
	}
	return Boolean(app_is_local && LOCAL_API.test(api_url));
};

// where a browser goes when the backend says its dev mode ended (graphql.js does the same inline). Added for jument's
// form file upload (a REST call), so every caller builds the same link
export const dev_mode_ended_path = (reason, back = "/") =>
	`/apis/dev-mode?ended=${encodeURIComponent(reason || "expired")}&back=${encodeURIComponent(back || "/")}`;

// the server cache's key prefix for a browser in dev mode: its entries never mix with the live site's
export const dev_cache_prefix_of = (values = {}) =>
	values[DEV_MODE_COOKIES.token] ? `dev:${values[DEV_MODE_COOKIES.site] || "-"}:${values[DEV_MODE_COOKIES.api_url] || "-"}:` : "";

/*
 * The file behind a next/image optimizer url (`/_next/image/?url=<encoded>&w=..&q=..`), for dev mode to load it
 * directly: a replica's files sit in its own bucket folder, which a site's `images.remotePatterns` usually does not
 * list, so the optimizer refuses them. -> the absolute http(s) url inside, or null (another url, or a local file the
 * optimizer serves fine).
 */
export const optimizer_file_url = (src, base = "http://localhost") => {
	if (!src) {
		return null;
	}
	let url;
	try {
		url = new URL(src, base);
	} catch {
		return null;
	}
	if (!/^\/_next\/image\/?$/.test(url.pathname)) {
		return null;
	}
	const file = url.searchParams.get("url") || "";
	return /^https?:\/\//i.test(file) ? file : null;
};

/*
 * What a page in dev mode was rendered with: the dev mode cookies, in one string. The DEV button compares it again while
 * the tab is open (cookies are the browser's, not the tab's): another tab leaving dev mode (?off=1), or switching replica
 * or API, changes it, and this tab reloads instead of showing the DEV button over another site's data.
 * values: { cookie name: value } -> string
 */
export const dev_cookie_snapshot = (values = {}) =>
	[DEV_MODE_COOKIES.token, DEV_MODE_COOKIES.site, DEV_MODE_COOKIES.api, DEV_MODE_COOKIES.api_url].map((name) => values[name] || "").join("|");
